/*
 * Copyright (c) 2022-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { Box, Typography } from '@mui/material';
import { useOdysseyDesignTokens } from '@okta/odyssey-react-mui';
import { FunctionComponent, h } from 'preact';
import { AuthCoinProps } from 'src/types';

import BrowserFeatures from '../../../../util/BrowserFeatures';
import { loc } from '../../util';
import AuthCoin from '../AuthCoin/AuthCoin';
import { getAuthCoinConfiguration } from '../AuthCoin/authCoinConfigUtil';
import Image from '../Image';

// TODO: maybe extract to util class if used reused
const shouldRenderAuthCoin = (
  props?: AuthCoinProps,
  autoRedirect?: boolean,
): boolean => {
  // Hide auth coin on Safari when auto-redirecting with external_idp custom logo
  if (
    autoRedirect
    && BrowserFeatures.isSafari()
    && props?.authenticatorKey === 'external_idp'
    && props?.url
  ) {
    return false;
  }

  const authCoinConfiguration = getAuthCoinConfiguration();
  const authCoinConfigByAuthKey = props?.authenticatorKey
    && authCoinConfiguration[props?.authenticatorKey];

  return typeof authCoinConfigByAuthKey !== 'undefined'
    || typeof props?.url !== 'undefined';
};

export type AuthHeaderProps = {
  logo?: string;
  logoText?: string;
  brandName?: string;
  authCoinProps?: AuthCoinProps;
  autoRedirect?: boolean;
};
const AuthHeader: FunctionComponent<AuthHeaderProps> = ({
  logo,
  logoText,
  brandName,
  authCoinProps,
  autoRedirect,
}) => {
  const showAuthCoin = shouldRenderAuthCoin(authCoinProps, autoRedirect);
  const containerTestIds = `okta-sign-in-header auth-header ${showAuthCoin ? 'authCoinSpacing' : ''}`;
  const tokens = useOdysseyDesignTokens();

  function renderAuthCoin() {
    return (showAuthCoin && authCoinProps) && (
      <AuthCoin
        authenticatorKey={authCoinProps.authenticatorKey}
        url={authCoinProps.url}
        theme={authCoinProps.theme}
        displayName={authCoinProps.displayName}
        sx={{
          margin: 'auto',
          insetBlockStart: '26px',
          borderRadius: '24px',
          backgroundColor: tokens.HueNeutralWhite,
        }}
      />
    );
  }

  return (
    <Box
      data-se={containerTestIds}
      sx={{
        paddingBlockStart: tokens.Spacing5,
        paddingBlockEnd: showAuthCoin ? tokens.Spacing1 : tokens.Spacing5,
        paddingInlineStart: tokens.Spacing6,
        paddingInlineEnd: tokens.Spacing6,
        borderBlockEnd: `1px solid ${tokens.BorderColorDisplay}`,
        '& h1': {
          lineHeight: 0,
          marginBlock: 0,
          textAlign: 'center',
        },
      }}
    >
      <Typography
        variant="h1"
        role="presentation"
        tabIndex={-1}
      >
        { logo && (
          <Image
            alt={logoText || brandName || loc('logo.default.alt.text', 'login')}
            src={logo}
            testId="auth-org-logo"
            maxWidth="200px"
            maxHeight="40px"
          />
        )}
      </Typography>
      { renderAuthCoin() }
    </Box>
  );
};

export default AuthHeader;
